import Link from "next/link";
import { ChevronRight } from "lucide-react";

/**
 * Shared white card surface for home dashboard sections. Sits on the warm cream
 * page background (see app/page.js) — a soft, premium lift where the hairline
 * border does most of the work and the shadow is barely-there (not the dated
 * heavy drop shadow). Reuse everywhere a section's content needs a panel.
 */
export const PANEL =
  "rounded-2xl border border-gray-100 bg-white shadow-[0_1px_3px_rgba(17,24,39,0.04)]";

/**
 * Shared section header for the home dashboard sections. Optional `icon`
 * renders a small coral scanning-anchor tile; the action link's chevron slides
 * on hover. Keeps a calm, scannable rhythm across rep + team views.
 */
export function SectionHeader({
  title,
  count,
  meta,
  actionLabel,
  actionHref,
  icon: Icon,
}) {
  return (
    <div className="mb-2 flex items-center justify-between gap-4">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold tracking-tight text-gray-900">
        {Icon && (
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600">
            <Icon className="h-3.5 w-3.5" strokeWidth={2} />
          </span>
        )}
        {title}
        {count != null && (
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-gray-500">
            {count}
          </span>
        )}
      </h2>
      <div className="flex items-center gap-3">
        {meta && <span className="text-[12.5px] text-gray-500">{meta}</span>}
        {actionLabel &&
          (actionHref ? (
            <Link
              href={actionHref}
              className="group inline-flex items-center gap-0.5 text-[12.5px] font-medium text-gray-600 transition-colors hover:text-brand-700"
            >
              {actionLabel}
              <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          ) : (
            <span className="text-[12.5px] font-medium text-gray-500">
              {actionLabel}
            </span>
          ))}
      </div>
    </div>
  );
}

/**
 * Severity tones shared by the card-less row primitives (InitialsTile,
 * StatusLabel) so a row's urgency reads consistently across its avatar tile and
 * status label.
 *   rose  → declining / negative   amber → low score / warning   default → neutral
 */
export const ROW_TONE = {
  rose: {
    tile: "bg-rose-50 text-rose-600",
    label: "text-rose-600",
    dot: "bg-rose-500",
  },
  amber: {
    tile: "bg-amber-50 text-amber-700",
    label: "text-amber-700",
    dot: "bg-amber-500",
  },
  default: {
    tile: "bg-gray-100 text-gray-600",
    label: "text-gray-500",
    dot: "bg-gray-400",
  },
};

function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

/**
 * Editorial section header for flat (card-less) ranked lists: an uppercase
 * letter-spaced title, an optional coral count, a hairline rule that fills the
 * gap, and an optional action link. Pairs with rows that sit directly on the
 * cream page background (see AttentionList / RolloutList / TestsDue).
 */
export function RuleHeader({ title, count, meta, actionLabel, actionHref }) {
  return (
    <div className="mb-3 flex items-center gap-4">
      <h2 className="flex shrink-0 items-center gap-2.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-gray-700">
        {title}
        {count != null && (
          <span className="tabular-nums text-brand-600">
            {String(count).padStart(2, "0")}
          </span>
        )}
      </h2>
      <span aria-hidden className="h-px flex-1 bg-gray-200" />
      {meta && (
        <span className="shrink-0 text-[12.5px] text-gray-500">{meta}</span>
      )}
      {actionLabel &&
        (actionHref ? (
          <Link
            href={actionHref}
            className="group inline-flex shrink-0 items-center gap-0.5 text-[12.5px] font-medium text-gray-600 transition-colors hover:text-brand-700"
          >
            {actionLabel}
            <ChevronRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Link>
        ) : (
          <span className="shrink-0 text-[12.5px] font-medium text-gray-500">
            {actionLabel}
          </span>
        ))}
    </div>
  );
}

/** Rounded-square initials avatar, tinted by the row's severity tone. */
export function InitialsTile({ name, tone = "default" }) {
  const t = ROW_TONE[tone] || ROW_TONE.default;
  return (
    <span
      className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[11px] font-semibold tracking-wide ${t.tile}`}
    >
      {initials(name) || "?"}
    </span>
  );
}

/**
 * Status as a borderless dot + colored uppercase label — deliberately NOT a
 * boxed pill, so on a clickable row it reads as a status without looking like a
 * second button competing with the row for the tap.
 */
export function StatusLabel({ children, tone = "default" }) {
  const t = ROW_TONE[tone] || ROW_TONE.default;
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider ${t.label}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} aria-hidden />
      {children}
    </span>
  );
}

/**
 * Two-tone progress bar (completed + in-progress). The fill grows from the
 * left on mount via `.animate-grow-x` (transform-based — see globals.css). Set
 * `animate={false}` to render static.
 */
export function ProgressBar({
  value = 0,
  secondary = 0,
  className = "",
  animate = true,
}) {
  const clampedValue = Math.min(100, Math.max(0, value));
  const clampedSecondary = Math.min(100 - clampedValue, Math.max(0, secondary));
  const total = clampedValue + clampedSecondary;
  // Inner segments are sized relative to the filled portion so the whole fill
  // can scaleX from one origin without the segments drifting apart.
  const valueShare = total > 0 ? (clampedValue / total) * 100 : 0;
  const secondaryShare = total > 0 ? (clampedSecondary / total) * 100 : 0;

  return (
    <span
      className={`flex h-1.5 overflow-hidden rounded-full bg-gray-100 ${className}`}
    >
      <span
        className={`flex h-full ${animate ? "animate-grow-x" : ""}`}
        style={{ width: `${total}%` }}
      >
        <span
          className="h-full bg-emerald-500"
          style={{ width: `${valueShare}%` }}
        />
        {secondaryShare > 0 && (
          <span
            className="h-full bg-amber-400"
            style={{ width: `${secondaryShare}%` }}
          />
        )}
      </span>
    </span>
  );
}
